import { ActivityTracker, NamespaceLoggerAdapter } from '@fluidx/telemetry';
import { getDocumentHiddenResult, startDocumentHiddenTracker, unregisterDocumentHiddenTracker } from '@fluidx/utilities';
import { HostActivity, HostActivityNameOverrides } from './HostActivity';
const endToEndActivitySuffix = 'EndToEnd';
/**
 * A cumulative tracker that helps FFX team to measure perf and possibly reliability of various events happening in the host app during create/load of fluid components.
 */
export class HostActivityTracker {
    constructor(activityType, logger, additionalSetup, additionalCleanup, 
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any
-- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */
    getExtraPropsToLog) {
        this.additionalCleanup = additionalCleanup;
        this.getExtraPropsToLog = getExtraPropsToLog;
        additionalSetup?.();
        this.logger = new NamespaceLoggerAdapter('HostTracker', logger);
        this.activityTracker = ActivityTracker.start(activityType, this.logger, {
            addUserTiming: true,
            isAPerformanceMetric: [HostActivity.RenderComponent, HostActivityNameOverrides.RenderComponent].includes(activityType) ||
                activityType.endsWith(endToEndActivitySuffix)
        });
    }
    /** Shared implementation  */
    /* eslint-disable-next-line no-restricted-syntax
  -- TODO: Suppression added when adding rule to forbid static methods. Consider using a free function instead, as static methods add considerable bundle bloat. */
    static startScenario(logger, scenarioType, scenarioCallback, getAdditionalTrackerProps) {
        if (scenarioCallback) {
            const scenarioTracker = HostActivityTracker.startScenario(logger, scenarioType);
            return new Promise(async (resolve, reject) => {
                try {
                    const results = await scenarioCallback(scenarioTracker);
                    scenarioTracker.setSuccess(getAdditionalTrackerProps?.());
                    resolve(results);
                }
                catch (error) {
                    scenarioTracker.setFailure(error, getAdditionalTrackerProps?.());
                    reject(error);
                }
            });
        }
        let documentHiddenTrackerId;
        return new HostActivityTracker(
        /** Complete name of the activity scenario */
        scenarioType + endToEndActivitySuffix, logger, 
        /** additionalSetup*/
        () => {
            documentHiddenTrackerId = startDocumentHiddenTracker();
        }, 
        /** additionalCleanup*/
        () => {
            if (documentHiddenTrackerId) {
                unregisterDocumentHiddenTracker(documentHiddenTrackerId);
                documentHiddenTrackerId = undefined;
            }
        }, 
        /** getExtraPropsToLog*/
        () => ({
            documentHidden: documentHiddenTrackerId
                ? getDocumentHiddenResult(documentHiddenTrackerId, true /* unregister */) > -1
                : ''
        }));
    }
    /** Shared implementation */
    /* eslint-disable-next-line no-restricted-syntax
  -- TODO: Suppression added when adding rule to forbid static methods. Consider using a free function instead, as static methods add considerable bundle bloat. */
    static startActivity(logger, activityType, activityCallback) {
        if (activityCallback) {
            const activityTracker = HostActivityTracker.startActivity(logger, activityType);
            return new Promise(async (resolve, reject) => {
                try {
                    const results = await activityCallback();
                    activityTracker.setSuccess();
                    resolve(results);
                }
                catch (error) {
                    activityTracker.setFailure(error);
                    reject(error);
                }
            });
        }
        return new HostActivityTracker(HostActivityNameOverrides[activityType] || activityType, logger);
    }
    /**
     * Call this method to end the activity and send the success status
     * @param props additional props that hosts would like to pass in to our telemetry.
     */
    setSuccess(props) {
        if (this.activityTracker) {
            this.activityTracker.setResult(true, 
            /* additionalProps */
            {
                ...props,
                ...this.getExtraPropsToLog?.()
            });
            this.dispose();
        }
    }
    /**
     * Call this method to end the activity and send the failure status
     * @param error Error object thrown
     * @param props Additional properties that will be logged.
     */
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any
  -- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */
    setFailure(error, props) {
        if (this.activityTracker) {
            this.activityTracker.setResult(false, 
            /* additionalProps */
            {
                ...props,
                ...this.getExtraPropsToLog?.(),
                errorType: error?.errorType
            }, error);
            this.dispose();
        }
    }
    /**
     * If activity did not get a chance to complete (succeed or fail),
     * call cancel so that we can still capture the cancellation of the event
     */
    cancel() {
        this.dispose();
    }
    /**
     * Disposes the activity and frees up the resources in the current object
     */
    dispose() {
        this.additionalCleanup?.();
        // If the activity hasn't already resolved/cancelled, this will cancel it (otherwise, it's a no-op).
        this.activityTracker?.dispose();
        this.activityTracker = undefined;
        this.logger = undefined;
    }
}
//# sourceMappingURL=HostActivityTracker.js.map